import * as React from 'react';
import themePrimary from '../../styles/themePrimary';
import { Typography, makeStyles, useMediaQuery, Dialog, AppBar, IconButton, Toolbar, Slide } from '@material-ui/core';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import CloseIcon from '@material-ui/icons/Close';
import { TransitionProps } from '@material-ui/core/transitions';
import TextField, { TextFieldProps, TextFieldLabelPosition, TextFieldChangeHandler } from '../common/TextField';
import FullWidthButton, { FullWidthButtonProps, FullWidthButtonSize } from '../common/Buttons/FullWidthButton';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import { ElementDimensions } from '../../styles/ElementDimensions';
import { Skin } from '../../models/common/Skin';

const useStyles = makeStyles({
    appBar: {
        height: ElementDimensions.dialogBarHeight + 'px',
        position: 'relative',
        backgroundColor: props => (props as Skin).primary_main_color + ' !important',
    },
    closeButton: {
        height: '30px',
        width: '30px',
        color: themePrimary.palette.primary.contrastText,
    },
    details: {
        position: 'absolute',
        top: ElementDimensions.dialogBarHeight + 'px',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    dialog: {
        backgroundColor: themePrimary.palette.secondary.light,
    },
    dialogLandscape: {
        backgroundColor: themePrimary.palette.secondary.light,
        left: 'calc(100vw *.3) !important',
        width: 'calc(100vw *.7)',
    },
    instructionText: {
        padding: ElementDimensions.padding20 + 'px',
        fontFamily: 'LeagueSpartan-Regular !important',
    },
    textFieldGrid: {
        display: 'grid',
        gridTemplateColumns: '50% 50%',
        gridRowGap: '30px',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '50px',
    },
    textLabel: {
        fontWeight: 'bold',
        marginTop: 'auto',
        marginBottom: 'auto',
        marginRight: ElementDimensions.margin10 + 'px',
    },
    title: {
        marginLeft: ElementDimensions.margin10 + 'px',
        flex: 1,
        color: themePrimary.palette.primary.contrastText,
    },
});

export type DemoRoomChargeDialogCloseClickHandler = (event: React.MouseEvent<HTMLElement>) => any;
export type DemoRoomChargeDialogCompleteHandler = (event: React.MouseEvent<HTMLElement>) => any;

export interface DemoRoomChargeDialogProps {
    open: boolean;
    onCloseHandler: DemoRoomChargeDialogCloseClickHandler;
    onCompleteHandler: DemoRoomChargeDialogCompleteHandler;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children?: React.ReactElement },
    ref: React.Ref<unknown>,
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const DemoRoomChargeDialog = (props: DemoRoomChargeDialogProps) => {
    const isPortrait = true;
    const { open, onCloseHandler, onCompleteHandler } = props;
    const [lastName, setLastName] = React.useState<string>('');
    const [roomNumber, setRoomNumber] = React.useState<string>('');
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    const handleClose = (event: React.MouseEvent<HTMLElement>) => {
        onCloseHandler(event);
    };

    const handleNameChange: TextFieldChangeHandler = (newValue: string) => {
        setLastName(newValue)
    };

    const nameProps: TextFieldProps = {
        type: null,
        label: null,
        text: lastName,
        width: 150,
        disabled: false,
        labelPosition: TextFieldLabelPosition.Left,
        onChangeHandler: handleNameChange,
    }

    const handleRoomChange: TextFieldChangeHandler = (newValue: string) => {
        setRoomNumber(newValue)
    };

    const roomProps: TextFieldProps = {
        type: null,
        label: null,
        text: roomNumber,
        width: 150,
        disabled: false,
        labelPosition: TextFieldLabelPosition.Left,
        onChangeHandler: handleRoomChange,
    }

    const handleCompleteButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        if (lastName.trim() !== "") {
            if (roomNumber.trim() !== "") {
                if (lastName.toLowerCase() === "jones") {
                    setErrorMessage("Unable to locate guest record. Please check the last name and room number entered.")
                    setErrorDialogOpen(true)
                }
                else if (lastName.toLowerCase() === "smith") {
                    setErrorMessage("It looks like there is an issue charging to your room. Please contact the server.")
                    setErrorDialogOpen(true)
                }
                else {
                    onCompleteHandler(event)
                }
            }
            else {
                setErrorMessage("Please enter a room number.")
                setErrorDialogOpen(true)
            }
        }
        else {
            setErrorMessage("Please enter a last name.")
            setErrorDialogOpen(true)
        }
    }

    const fullWidthButtonProps: FullWidthButtonProps = {
        disabled: (roomNumber === '' || lastName === ''),
        label: "COMPLETE",
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: handleCompleteButtonClick,
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: false,
        onCloseHandler: handleErrorDialogClose,
    }

    return (
        <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition} className={isPortrait === true ? classes.dialog : classes.dialogLandscape}>
            <AppBar className={classes.appBar}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                        <CloseIcon className={classes.closeButton} />
                    </IconButton>
                    <Typography variant="h6" className={classes.title}>
                        Charge To Room
                    </Typography>
                </Toolbar>
            </AppBar>
            <div>
                <Typography variant='h6' className={classes.instructionText}>
                    Please enter your last name and room number.
                </Typography>
            </div>
            <div className={classes.textFieldGrid}>
                <Typography variant="body1" className={classes.textLabel}>
                    Last Name: 
                </Typography>
                <TextField 
                    type={nameProps.type}
                    label={nameProps.label}
                    text={nameProps.text}
                    width={nameProps.width}
                    disabled={nameProps.disabled}
                    labelPosition={nameProps.labelPosition}
                    onChangeHandler={nameProps.onChangeHandler} />
                <Typography variant="body1" className={classes.textLabel}>
                    Room Number:  
                </Typography>
                <TextField 
                    type={roomProps.type}
                    label={roomProps.label}
                    text={roomProps.text}
                    width={roomProps.width}
                    disabled={roomProps.disabled}
                    labelPosition={roomProps.labelPosition}
                    onChangeHandler={roomProps.onChangeHandler} />
            </div>
            <FullWidthButton 
                disabled={fullWidthButtonProps.disabled}
                label={fullWidthButtonProps.label}
                alternateColor={fullWidthButtonProps.alternateColor}
                size={fullWidthButtonProps.size}
                onClickHandler={fullWidthButtonProps.onClickHandler}/>

            <ErrorDialog 
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />
        </Dialog>
    );
};

export default DemoRoomChargeDialog;